import { Field, Icon, TextInput } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/lib/useViewport";
import { useState } from "react";

export const LoginScreen = () => {
  const mobile = useIsMobile();
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [hover, setHover] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithEmail(email, pw);
    } catch {
      setError("이메일 또는 비밀번호가 올바르지 않습니다.");
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch {
      setError("Google 로그인에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ height: "100vh", display: "flex", background: "var(--bg-shell)" }}>
      {/* Left brand panel */}
      <div
        style={{
          flex: 1,
          position: "relative",
          display: mobile ? "none" : "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "56px 56px 48px",
          background: "linear-gradient(160deg, var(--bg-brand-from) 0%, var(--bg-base) 56%)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: -120,
            top: -80,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(49,130,246,0.16), transparent 65%)",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
          <img
            src="/logo.png"
            alt="한부금속"
            width={38}
            height={38}
            style={{ borderRadius: 9, display: "block" }}
          />
          <div style={{ fontWeight: 900, fontSize: 20 }}>
            한부금속 <span style={{ color: "var(--brand)" }}>CMS</span>
          </div>
        </div>
        <div style={{ position: "relative", maxWidth: 480 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "2px",
              color: "var(--brand)",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            Cost Management System
          </div>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 900,
              fontSize: 42,
              lineHeight: 1.18,
              letterSpacing: "-0.02em",
            }}
          >
            견적과 원가를
            <br />한 곳에서.
          </div>
          <p style={{ fontSize: 15.5, color: "var(--text-muted)", lineHeight: 1.7, marginTop: 20 }}>
            엑셀로 흩어진 견적을 통합 관리하고, 원가·마진을 실시간으로 계산합니다. 작성 시간은
            줄이고, 이력은 체계적으로.
          </p>
          <div style={{ display: "flex", gap: 28, marginTop: 36 }}>
            {(
              [
                ["80+", "월 견적 처리"],
                ["3분", "평균 작성 시간"],
                ["100%", "이력 자동 보존"],
              ] as [string, string][]
            ).map(([n, l]) => (
              <div key={l}>
                <div
                  style={{
                    fontFamily: "var(--font-title)",
                    fontWeight: 900,
                    fontSize: 28,
                    color: "var(--text-base)",
                  }}
                >
                  {n}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--text-dim)", marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-fainter)", position: "relative" }}>
          © 2026 한부금속 · 사내 전용 시스템
        </div>
      </div>

      {/* Right login form */}
      <div
        style={{
          width: mobile ? "100%" : 480,
          flexShrink: 0,
          background: "var(--bg-base)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: mobile ? "0 20px" : "0 56px",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 800,
            fontSize: 26,
            marginBottom: 6,
          }}
        >
          로그인
        </div>
        <div style={{ fontSize: 13.5, color: "var(--text-muted)", marginBottom: 34 }}>
          등록된 사내 계정으로 접속하세요
        </div>

        <Field label="이메일" en="Email" style={{ marginBottom: 18 }}>
          <TextInput
            value={email}
            onChange={setEmail}
            placeholder="name@hansung.co.kr"
            style={{ height: 48 }}
          />
        </Field>
        <Field label="비밀번호" en="Password" style={{ marginBottom: 14 }}>
          <TextInput
            value={pw}
            onChange={setPw}
            type="password"
            placeholder="비밀번호"
            style={{ height: 48 }}
          />
        </Field>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: "var(--brand)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="check" size={12} color="#ffffff" stroke={3} />
            </span>
            로그인 유지
          </label>
          <span style={{ fontSize: 13, color: "var(--brand)", cursor: "pointer", fontWeight: 700 }}>
            비밀번호 찾기
          </span>
        </div>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(243,114,127,0.1)",
              border: "1px solid rgba(243,114,127,0.35)",
              color: "var(--text-negative)",
              fontSize: 12.5,
              fontWeight: 700,
              padding: "10px 14px",
              borderRadius: 12,
              marginBottom: 14,
            }}
          >
            <Icon name="alert" size={15} color="var(--text-negative)" />
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            height: 52,
            borderRadius: 14,
            border: 0,
            background: hover ? "var(--brand-hover)" : "var(--brand)",
            color: "#ffffff",
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: 15.5,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
            transition: "background 120ms",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          로그인 <Icon name="arrowRight" size={18} stroke={2.4} />
        </button>

        <button
          type="button"
          onClick={google}
          disabled={busy}
          style={{
            height: 48,
            marginTop: 12,
            borderRadius: 14,
            border: 0,
            background: "var(--bg-surface-2)",
            color: "var(--text-base)",
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: 14,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          Google 계정으로 로그인
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 24,
            fontSize: 12,
            color: "var(--text-fainter)",
            justifyContent: "center",
          }}
        >
          <Icon name="dot" size={12} color="var(--brand)" />
          Firebase Auth · 이메일 / 비밀번호 인증
        </div>
      </div>
    </div>
  );
};
